import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  SelectChangeEvent,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import InvoiceService from '../services/invoiceService';
import indexedDBService from '../services/IndexedDBService';
import { formatCurrency, formatDate } from '../utils/formatters';

interface ReportData {
  id: string;
  date: string;
  invoiceNumber: string;
  clientName: string;
  clientGSTIN: string;
  taxableValue: number;
  cgst: number;
  sgst: number;
  igst: number;
  totalAmount: number;
  type: 'b2b' | 'b2c';
}

const Reports: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [filteredData, setFilteredData] = useState<ReportData[]>([]);
  const [purchaseData, setPurchaseData] = useState<any[]>([]);
  const [filteredPurchases, setFilteredPurchases] = useState<any[]>([]);

  // Presets and Date range filters
  const [preset, setPreset] = useState<'month' | 'quarter' | 'all' | 'custom'>('all');
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedQuarter, setSelectedQuarter] = useState<number>(Math.floor(new Date().getMonth() / 3) + 1);
  const [customFromDate, setCustomFromDate] = useState<string>('');
  const [customToDate, setCustomToDate] = useState<string>('');

  const [gstType, setGstType] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Fetch report data
  const fetchReportData = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      // 1. Fetch Invoices
      const allInvoices = await InvoiceService.getInvoices(currentUser.uid);
      allInvoices.sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());

      const data = allInvoices.map(invoice => {
        const totalTax = invoice.tax || 0;
        let cgst = 0;
        let sgst = 0;
        let igst = 0;

        const taxType = invoice.taxType || 'gst';
        if (taxType.toLowerCase() === 'igst') {
          igst = totalTax;
        } else {
          cgst = totalTax / 2;
          sgst = totalTax / 2;
        }

        const clientObj = invoice.client as any;
        const clientName = typeof clientObj === 'object' && clientObj !== null
          ? (clientObj.name || 'Unknown Client')
          : (clientObj || 'Unknown Client');

        const gstin = invoice.clientDetails?.gstin
          || (typeof clientObj === 'object' && clientObj !== null ? clientObj.gstin : '')
          || '';

        return {
          id: invoice.id,
          date: invoice.date || new Date().toISOString().split('T')[0],
          invoiceNumber: invoice.invoiceNumber || '',
          clientName,
          clientGSTIN: gstin,
          taxableValue: (invoice.subtotal || 0) - (invoice.discount || 0),
          cgst,
          sgst,
          igst,
          totalAmount: invoice.amount || 0,
          type: gstin ? 'b2b' : 'b2c'
        } as ReportData;
      });

      // 2. Fetch Purchases (ITC)
      const allPurchases = await indexedDBService.getAllItems<any>('purchases');
      const userPurchases = allPurchases.filter(p => p.userId === currentUser.uid);
      userPurchases.sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());

      const mappedPurchases = userPurchases.map(p => {
        const totalTax = p.tax || p.taxAmount || 0;
        let cgst = 0;
        let sgst = 0;
        let igst = 0;

        const taxType = p.taxType || 'gst';
        if (taxType.toLowerCase() === 'igst') {
          igst = totalTax;
        } else {
          cgst = totalTax / 2;
          sgst = totalTax / 2;
        }

        const gstin = p.vendorGSTIN || p.vendorDetails?.gstin || '';

        return {
          id: p.id,
          date: p.date || new Date().toISOString().split('T')[0],
          purchaseNumber: p.purchaseNumber || p.billNumber || `PUR-${p.id?.substring(0, 5).toUpperCase()}`,
          vendorName: p.vendorName || p.vendor || 'Unknown Vendor',
          vendorGSTIN: gstin,
          taxableValue: (p.subtotal || p.taxableAmount || (p.amount || 0) - totalTax || 0),
          cgst,
          sgst,
          igst,
          totalAmount: p.total || p.amount || 0,
          type: gstin ? 'b2b' : 'b2c'
        };
      });

      setReportData(data);
      setFilteredData(data);
      setPurchaseData(mappedPurchases);
      setFilteredPurchases(mappedPurchases);
    } catch (error) {
      console.error('Error fetching report data:', error);
      setSnackbar({
        open: true,
        message: 'Error loading report data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on selected criteria
  const filterData = () => {
    let invoices = [...reportData];
    let purchases = [...purchaseData];

    const now = new Date();
    const currentYear = now.getFullYear();

    if (preset === 'month') {
      invoices = invoices.filter(item => {
        const d = new Date(item.date);
        return d.getFullYear() === currentYear && (d.getMonth() + 1) === selectedMonth;
      });
      purchases = purchases.filter(item => {
        const d = new Date(item.date);
        return d.getFullYear() === currentYear && (d.getMonth() + 1) === selectedMonth;
      });
    } else if (preset === 'quarter') {
      const startMonth = (selectedQuarter - 1) * 3 + 1;
      const endMonth = startMonth + 2;
      invoices = invoices.filter(item => {
        const d = new Date(item.date);
        const m = d.getMonth() + 1;
        return d.getFullYear() === currentYear && m >= startMonth && m <= endMonth;
      });
      purchases = purchases.filter(item => {
        const d = new Date(item.date);
        const m = d.getMonth() + 1;
        return d.getFullYear() === currentYear && m >= startMonth && m <= endMonth;
      });
    } else if (preset === 'custom') {
      if (customFromDate) {
        invoices = invoices.filter(item => item.date >= customFromDate);
        purchases = purchases.filter(item => item.date >= customFromDate);
      }
      if (customToDate) {
        invoices = invoices.filter(item => item.date <= customToDate);
        purchases = purchases.filter(item => item.date <= customToDate);
      }
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      invoices = invoices.filter(item =>
        (item.invoiceNumber && item.invoiceNumber.toLowerCase().includes(query)) ||
        (item.clientName && item.clientName.toLowerCase().includes(query)) ||
        (item.clientGSTIN && item.clientGSTIN.toLowerCase().includes(query))
      );
      purchases = purchases.filter(item =>
        (item.purchaseNumber && item.purchaseNumber.toLowerCase().includes(query)) ||
        (item.vendorName && item.vendorName.toLowerCase().includes(query)) ||
        (item.vendorGSTIN && item.vendorGSTIN.toLowerCase().includes(query))
      );
    }

    setFilteredData(invoices);
    setFilteredPurchases(purchases);
  };



  useEffect(() => {
    fetchReportData();
  }, [currentUser]);

  useEffect(() => {
    filterData();
  }, [preset, selectedMonth, selectedQuarter, customFromDate, customToDate, searchQuery, reportData, purchaseData]);

  // Helper to calculate sales summaries
  const getSalesSummary = () => {
    let taxable = 0, cgst = 0, sgst = 0, igst = 0, total = 0;
    filteredData.forEach(item => {
      taxable += item.taxableValue;
      cgst += item.cgst;
      sgst += item.sgst;
      igst += item.igst;
      total += item.totalAmount;
    });
    return { taxable, cgst, sgst, igst, total };
  };

  // Helper to calculate purchase summaries
  const getPurchasesSummary = () => {
    let taxable = 0, cgst = 0, sgst = 0, igst = 0, total = 0;
    filteredPurchases.forEach(item => {
      taxable += item.taxableValue;
      cgst += item.cgst;
      sgst += item.sgst;
      igst += item.igst;
      total += item.totalAmount;
    });
    return { taxable, cgst, sgst, igst, total };
  };

  const salesSum = getSalesSummary();
  const purSum = getPurchasesSummary();

  const netCgst = salesSum.cgst - purSum.cgst;
  const netSgst = salesSum.sgst - purSum.sgst;
  const netIgst = salesSum.igst - purSum.igst;
  const netLiability = netCgst + netSgst + netIgst;

  // Custom CSV Exporter for GSTR-1 B2B and B2C
  const exportGstr1CSV = () => {
    const b2bList = filteredData.filter(i => i.type === 'b2b');
    const b2cList = filteredData.filter(i => i.type === 'b2c');

    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "GSTR-1 REPORT (Outward Supplies Sales)\n\n";

    csvContent += "SECTION 1: B2B INVOICES (Registered Supplies)\n";
    csvContent += "Invoice Date,Invoice Number,Client Name,Client GSTIN,Taxable Value,CGST,SGST,IGST,Total Amount\n";
    b2bList.forEach(i => {
      csvContent += `${formatDate(i.date)},${i.invoiceNumber},${i.clientName},${i.clientGSTIN},${i.taxableValue.toFixed(2)},${i.cgst.toFixed(2)},${i.sgst.toFixed(2)},${i.igst.toFixed(2)},${i.totalAmount.toFixed(2)}\n`;
    });

    csvContent += "\nSECTION 2: B2C INVOICES (Unregistered Supplies)\n";
    csvContent += "Invoice Date,Invoice Number,Client Name,Taxable Value,CGST,SGST,IGST,Total Amount\n";
    b2cList.forEach(i => {
      csvContent += `${formatDate(i.date)},${i.invoiceNumber},${i.clientName},${i.taxableValue.toFixed(2)},${i.cgst.toFixed(2)},${i.sgst.toFixed(2)},${i.igst.toFixed(2)},${i.totalAmount.toFixed(2)}\n`;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `GSTR_1_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSnackbar({ open: true, message: 'GSTR-1 CSV exported successfully!', severity: 'success' });
  };

  // Custom CSV Exporter for GSTR-3B Summary
  const exportGstr3bCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "GSTR-3B SUMMARY REPORT\n\n";
    
    csvContent += "Table 3.1: Details of Outward Taxable Supplies (Sales)\n";
    csvContent += "Category,Taxable Value,Integrated Tax (IGST),Central Tax (CGST),State/UT Tax (SGST)\n";
    csvContent += `Outward Supplies,${salesSum.taxable.toFixed(2)},${salesSum.igst.toFixed(2)},${salesSum.cgst.toFixed(2)},${salesSum.sgst.toFixed(2)}\n\n`;

    csvContent += "Table 4: Eligible Input Tax Credit (ITC from Purchases)\n";
    csvContent += "Category,Taxable Value,Integrated Tax (IGST),Central Tax (CGST),State/UT Tax (SGST)\n";
    csvContent += `Inward Supplies (Eligible ITC),${purSum.taxable.toFixed(2)},${purSum.igst.toFixed(2)},${purSum.cgst.toFixed(2)},${purSum.sgst.toFixed(2)}\n\n`;

    csvContent += "Table 5: Net GST Payable (Liability after ITC Offset)\n";
    csvContent += "Tax Head,Liability (Outward),Credit Offset (ITC),Net Tax Payable\n";
    csvContent += `CGST,${salesSum.cgst.toFixed(2)},${purSum.cgst.toFixed(2)},${netCgst.toFixed(2)}\n`;
    csvContent += `SGST,${salesSum.sgst.toFixed(2)},${purSum.sgst.toFixed(2)},${netSgst.toFixed(2)}\n`;
    csvContent += `IGST,${salesSum.igst.toFixed(2)},${purSum.igst.toFixed(2)},${netIgst.toFixed(2)}\n`;
    csvContent += `TOTAL,${(salesSum.cgst + salesSum.sgst + salesSum.igst).toFixed(2)},${(purSum.cgst + purSum.sgst + purSum.igst).toFixed(2)},${netLiability.toFixed(2)}\n`;

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `GSTR_3B_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSnackbar({ open: true, message: 'GSTR-3B CSV exported successfully!', severity: 'success' });
  };

  const exportToCSV = (filtered: boolean = false) => {
    const dataToExport = filtered ? filteredData : reportData;

    const headers = [
      'Date',
      'Invoice Number',
      'Client Name',
      'GSTIN',
      'Taxable Value',
      'CGST',
      'SGST',
      'IGST',
      'Total Amount',
      'Type'
    ];

    const rows = dataToExport.map(item => [
      formatDate(item.date),
      item.invoiceNumber,
      item.clientName,
      item.clientGSTIN || 'N/A',
      item.taxableValue.toFixed(2),
      item.cgst.toFixed(2),
      item.sgst.toFixed(2),
      item.igst.toFixed(2),
      item.totalAmount.toFixed(2),
      item.type.toUpperCase()
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `gst_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSnackbar({ open: true, message: 'Export completed successfully!', severity: 'success' });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{
            background: 'linear-gradient(45deg, #10B981 30%, #3B82F6 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 800
          }}>
            GST Compliance Engine
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Generate dynamic GSTR-1 and GSTR-3B summary reports for tax filing and audits
          </Typography>
        </Box>
        <Box display="flex" gap={1.5} flexWrap="wrap">
          {activeTab === 2 && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportGstr1CSV}
              sx={{ background: 'linear-gradient(45deg, #10B981 30%, #059669 90%)', color: 'white', borderRadius: 2 }}
            >
              Export GSTR-1 CSV
            </Button>
          )}
          {activeTab === 3 && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportGstr3bCSV}
              sx={{ background: 'linear-gradient(45deg, #3B82F6 30%, #2563EB 90%)', color: 'white', borderRadius: 2 }}
            >
              Export GSTR-3B CSV
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => setExportDialogOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            Export All Data
          </Button>
        </Box>
      </Box>

      {/* Date & Filter Controls Card */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={2.5} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Preset</InputLabel>
              <Select
                value={preset}
                label="Date Preset"
                onChange={e => setPreset(e.target.value as any)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Available Data</MenuItem>
                <MenuItem value="month">Monthly Report</MenuItem>
                <MenuItem value="quarter">Quarterly Report</MenuItem>
                <MenuItem value="custom">Custom Date Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {preset === 'month' && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Select Month"
                  onChange={e => setSelectedMonth(Number(e.target.value))}
                  sx={{ borderRadius: 2 }}
                >
                  {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, idx) => (
                    <MenuItem key={m} value={idx + 1}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {preset === 'quarter' && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Quarter</InputLabel>
                <Select
                  value={selectedQuarter}
                  label="Select Quarter"
                  onChange={e => setSelectedQuarter(Number(e.target.value))}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value={1}>Q1 (Jan - Mar)</MenuItem>
                  <MenuItem value={2}>Q2 (Apr - Jun)</MenuItem>
                  <MenuItem value={3}>Q3 (Jul - Sep)</MenuItem>
                  <MenuItem value={4}>Q4 (Oct - Dec)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {preset === 'custom' && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth size="small" type="date" label="From Date"
                  InputLabelProps={{ shrink: true }} value={customFromDate}
                  onChange={e => setCustomFromDate(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth size="small" type="date" label="To Date"
                  InputLabelProps={{ shrink: true }} value={customToDate}
                  onChange={e => setCustomToDate(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={preset === 'all' ? 9 : 6}>
            <TextField
              fullWidth size="small" placeholder="Search invoices, clients, vendors or GSTIN..."
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} /> }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* KPI Highlight Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: '#F0F9FF' }}>
            <Typography variant="caption" color="primary.main" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
              Total Outward Sales
            </Typography>
            <Typography variant="h5" fontWeight="bold" mt={0.5}>
              {formatCurrency(salesSum.total)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Taxable Value: {formatCurrency(salesSum.taxable)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: '#ECFDF5' }}>
            <Typography variant="caption" color="success.main" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
              Total Inward Purchases
            </Typography>
            <Typography variant="h5" fontWeight="bold" mt={0.5}>
              {formatCurrency(purSum.total)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Taxable Value: {formatCurrency(purSum.taxable)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: '#FEF2F2' }}>
            <Typography variant="caption" color="error.main" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
              Outward GST Liabilities
            </Typography>
            <Typography variant="h5" fontWeight="bold" mt={0.5}>
              {formatCurrency(salesSum.cgst + salesSum.sgst + salesSum.igst)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              CGST + SGST + IGST
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={{
            p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider',
            bgcolor: netLiability >= 0 ? '#FFFBEB' : '#ECFDF5'
          }}>
            <Typography variant="caption" color={netLiability >= 0 ? 'warning.main' : 'success.main'} fontWeight={700} sx={{ textTransform: 'uppercase' }}>
              Net GST Payable
            </Typography>
            <Typography variant="h5" fontWeight="bold" mt={0.5} color={netLiability >= 0 ? 'error.main' : 'success.main'}>
              {formatCurrency(netLiability)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Outward GST Liability - Inward ITC
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{
            background: '#F9FAFB',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minWidth: 140, py: 2 },
            '& .Mui-selected': { color: 'primary.main', background: '#FFFFFF' }
          }}
        >
          <Tab label="GST Sales Ledger" />
          <Tab label="GST Purchase Ledger" />
          <Tab label="GSTR-1 Form" />
          <Tab label="GSTR-3B Summary" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={8}>
          <CircularProgress size={36} />
        </Box>
      ) : (
        <>
          {/* TAB 0: GST Sales Ledger */}
          {activeTab === 0 && (
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#F9FAFB' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Invoice #</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Client</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Client GSTIN</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Taxable Value</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>CGST</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>SGST</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>IGST</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Total Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.map(item => (
                      <TableRow key={item.id} hover>
                        <TableCell>{formatDate(item.date)}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{item.invoiceNumber}</TableCell>
                        <TableCell>{item.clientName}</TableCell>
                        <TableCell>{item.clientGSTIN || 'N/A'}</TableCell>
                        <TableCell>{formatCurrency(item.taxableValue)}</TableCell>
                        <TableCell>{formatCurrency(item.cgst)}</TableCell>
                        <TableCell>{formatCurrency(item.sgst)}</TableCell>
                        <TableCell>{formatCurrency(item.igst)}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(item.totalAmount)}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.type.toUpperCase()} size="small"
                            color={item.type === 'b2b' ? 'success' : 'primary'} variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredData.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                          No sales data found for the selected period.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* TAB 1: GST Purchase Ledger */}
          {activeTab === 1 && (
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#F9FAFB' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Purchase/Bill #</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Vendor</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Vendor GSTIN</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Taxable Value</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>CGST ITC</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>SGST ITC</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>IGST ITC</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Total Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPurchases.map(item => (
                      <TableRow key={item.id} hover>
                        <TableCell>{formatDate(item.date)}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{item.purchaseNumber}</TableCell>
                        <TableCell>{item.vendorName}</TableCell>
                        <TableCell>{item.vendorGSTIN || 'N/A'}</TableCell>
                        <TableCell>{formatCurrency(item.taxableValue)}</TableCell>
                        <TableCell>{formatCurrency(item.cgst)}</TableCell>
                        <TableCell>{formatCurrency(item.sgst)}</TableCell>
                        <TableCell>{formatCurrency(item.igst)}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(item.totalAmount)}</TableCell>
                      </TableRow>
                    ))}
                    {filteredPurchases.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                          No purchase (ITC) data found for the selected period.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* TAB 2: GSTR-1 Form */}
          {activeTab === 2 && (
            <Box display="flex" flexDirection="column" gap={3}>
              {/* B2B Invoices Section */}
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" fontWeight="bold">Section 4A, 4B, 4C - B2B Invoices</Typography>
                  <Chip
                    label={`${filteredData.filter(i => i.type === 'b2b').length} Invoices`}
                    color="success" size="small" sx={{ fontWeight: 600 }}
                  />
                </Box>
                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#F9FAFB' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Client GSTIN</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Client Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Taxable Value</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>CGST</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>SGST</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>IGST</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData.filter(i => i.type === 'b2b').map(item => (
                        <TableRow key={item.id}>
                          <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{item.clientGSTIN}</TableCell>
                          <TableCell>{item.clientName}</TableCell>
                          <TableCell>{formatCurrency(item.taxableValue)}</TableCell>
                          <TableCell>{formatCurrency(item.cgst)}</TableCell>
                          <TableCell>{formatCurrency(item.sgst)}</TableCell>
                          <TableCell>{formatCurrency(item.igst)}</TableCell>
                        </TableRow>
                      ))}
                      {filteredData.filter(i => i.type === 'b2b').length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                            No registered B2B supplies found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* B2C Section */}
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" fontWeight="bold">Section 7 - B2C (Others) Supplies</Typography>
                  <Chip
                    label={`${filteredData.filter(i => i.type === 'b2c').length} Invoices`}
                    color="primary" size="small" sx={{ fontWeight: 600 }}
                  />
                </Box>
                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#F9FAFB' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Taxable Value</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>CGST Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>SGST Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>IGST Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const b2cInvoices = filteredData.filter(i => i.type === 'b2c');
                        const taxableSum = b2cInvoices.reduce((acc, curr) => acc + curr.taxableValue, 0);
                        const cgstSum = b2cInvoices.reduce((acc, curr) => acc + curr.cgst, 0);
                        const sgstSum = b2cInvoices.reduce((acc, curr) => acc + curr.sgst, 0);
                        const igstSum = b2cInvoices.reduce((acc, curr) => acc + curr.igst, 0);
                        return (
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Aggregate Unregistered Supplies</TableCell>
                            <TableCell>{formatCurrency(taxableSum)}</TableCell>
                            <TableCell>{formatCurrency(cgstSum)}</TableCell>
                            <TableCell>{formatCurrency(sgstSum)}</TableCell>
                            <TableCell>{formatCurrency(igstSum)}</TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}

          {/* TAB 3: GSTR-3B Summary */}
          {activeTab === 3 && (
            <Box display="flex" flexDirection="column" gap={3}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight="bold" mb={2}>Table 3.1: Details of Outward Supplies & inward supplies liable to reverse charge</Typography>
                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#F9FAFB' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Nature of Supplies</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Total Taxable Value</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Integrated Tax (IGST)</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Central Tax (CGST)</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>State/UT Tax (SGST)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow hover>
                        <TableCell sx={{ fontWeight: 600 }}>(a) Outward taxable supplies (other than zero rated, nil rated and exempted)</TableCell>
                        <TableCell>{formatCurrency(salesSum.taxable)}</TableCell>
                        <TableCell>{formatCurrency(salesSum.igst)}</TableCell>
                        <TableCell>{formatCurrency(salesSum.cgst)}</TableCell>
                        <TableCell>{formatCurrency(salesSum.sgst)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight="bold" mb={2}>Table 4: Details of Eligible Input Tax Credit (ITC)</Typography>
                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#F9FAFB' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>ITC Category</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Total Taxable Value</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Integrated Tax (IGST)</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Central Tax (CGST)</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>State/UT Tax (SGST)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow hover>
                        <TableCell sx={{ fontWeight: 600 }}>(A) All Other ITC (From purchases & expenses)</TableCell>
                        <TableCell>{formatCurrency(purSum.taxable)}</TableCell>
                        <TableCell>{formatCurrency(purSum.igst)}</TableCell>
                        <TableCell>{formatCurrency(purSum.cgst)}</TableCell>
                        <TableCell>{formatCurrency(purSum.sgst)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: '#F9FAFB' }}>
                <Typography variant="h6" fontWeight="bold" mb={2}>Net Tax Liabilities (Payable in Cash after ITC offset)</Typography>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={4}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>CGST NET LIABILITY</Typography>
                      <Typography variant="h6" fontWeight="bold" mt={0.5} color={netCgst >= 0 ? 'error.main' : 'success.main'}>
                        {formatCurrency(netCgst)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>SGST NET LIABILITY</Typography>
                      <Typography variant="h6" fontWeight="bold" mt={0.5} color={netSgst >= 0 ? 'error.main' : 'success.main'}>
                        {formatCurrency(netSgst)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>IGST NET LIABILITY</Typography>
                      <Typography variant="h6" fontWeight="bold" mt={0.5} color={netIgst >= 0 ? 'error.main' : 'success.main'}>
                        {formatCurrency(netIgst)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
        </>
      )}

      {/* Export Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Export GST Report Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which scope of report data you would like to hand off to your accountant:
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setExportDialogOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              exportToCSV(true);
              setExportDialogOpen(false);
            }}
            variant="outlined"
            color="primary"
          >
            Export Filtered Period
          </Button>
          <Button
            onClick={() => {
              exportToCSV(false);
              setExportDialogOpen(false);
            }}
            variant="contained"
            color="primary"
          >
            Export All Records
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reports; 
